import React, { useCallback, useEffect, useState } from 'react';
import { useSurveyStore } from '../../surveyutilities/surveyStore';
import {
    PropsSection,
    PropsField,
    PropsToggle,
    PropsShell,
} from '../blockproperties/QuestionPropertiesShared';
import IconPickerField from './IconPickerField';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import settingsService from '../../../../../services/settings.service';

function getDoctorDisplayName(doctor) {
    const name = [doctor?.firstName, doctor?.lastName].filter(Boolean).join(' ').trim();
    return name || doctor?.username || 'Doctor';
}

function SurveySettings() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const survey = useSurveyStore((state) => state.survey);
    const setSurveySetting = useSurveyStore((state) => state.setSurveySetting);
    const [doctors, setDoctors] = useState([]);
    const [doctorsLoading, setDoctorsLoading] = useState(false);

    const setValue = (field, value) => setSurveySetting(field, value);

    const assignmentScope = survey.assignmentScope || 'CLINIC';

    const fetchDoctors = useCallback(async () => {
        if (!clinicId) {
            setDoctors([]);
            return;
        }
        setDoctorsLoading(true);
        try {
            const response = await settingsService.getUsersByRole(clinicId, 'ECP');
            setDoctors(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error(error);
            setDoctors([]);
        } finally {
            setDoctorsLoading(false);
        }
    }, [clinicId]);

    useEffect(() => {
        fetchDoctors();
    }, [fetchDoctors]);

    const handleAssignmentScopeChange = (event) => {
        const scope = event.target.value;
        setValue('assignmentScope', scope);
        if (scope === 'CLINIC') {
            setValue('assignedUserId', null);
            setValue('assignedUserName', null);
        }
    };

    const handleAssignedDoctorChange = (event) => {
        const userId = event.target.value || null;
        const doctor = doctors.find((item) => item.id === userId) || null;
        setValue('assignedUserId', userId);
        setValue('assignedUserName', doctor ? getDoctorDisplayName(doctor) : null);
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        return date.toISOString().slice(0, 16);
    };

    const handleDateChange = (field) => (event) => {
        const value = event.target.value;
        setValue(field, value ? new Date(value).toISOString() : null);
    };

    return (
        <PropsShell typeLabel="Survey settings" typeIcon="bi-sliders">
            <PropsSection title="Basics">
                <PropsField label="Survey name" hint="Shown to patients at the top of the survey">
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={survey.title || ''}
                        onChange={(e) => setValue('title', e.target.value)}
                    />
                </PropsField>

                <PropsField label="Description" hint="Optional supporting text under the title">
                    <textarea
                        className="form-control form-control-sm"
                        rows={2}
                        value={survey.description || ''}
                        onChange={(e) => setValue('description', e.target.value)}
                    />
                </PropsField>

                <PropsField label="Category">
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={survey.category || ''}
                        onChange={(e) => setValue('category', e.target.value)}
                    />
                </PropsField>
            </PropsSection>

            <PropsSection title="Assignment">
                <PropsField
                    label="Assign survey to"
                    hint="Clinic-wide surveys apply to everyone; doctor surveys are limited to one ECP"
                >
                    <select
                        className="form-select form-select-sm"
                        value={assignmentScope}
                        onChange={handleAssignmentScopeChange}
                    >
                        <option value="CLINIC">Entire clinic</option>
                        <option value="DOCTOR">Specific doctor</option>
                    </select>
                </PropsField>

                {assignmentScope === 'DOCTOR' ? (
                    <PropsField label="Doctor">
                        <select
                            className="form-select form-select-sm"
                            value={survey.assignedUserId || ''}
                            onChange={handleAssignedDoctorChange}
                            disabled={doctorsLoading}
                        >
                            <option value="">
                                {doctorsLoading ? 'Loading doctors…' : 'Select a doctor'}
                            </option>
                            {doctors.map((doctor) => (
                                <option key={doctor.id} value={doctor.id}>
                                    {getDoctorDisplayName(doctor)}
                                </option>
                            ))}
                        </select>
                    </PropsField>
                ) : null}
            </PropsSection>

            <PropsSection title="Experience">
                <PropsToggle
                    checked={survey.showProgressbar}
                    onChange={(e) => setValue('showProgressbar', e.target.checked)}
                    label="Show progress bar"
                />

                {survey.showProgressbar ? (
                    <PropsField label="Progress bar type">
                        <select
                            className="form-select form-select-sm"
                            value={survey.progressbarType || 'PERCENT'}
                            onChange={(e) => setValue('progressbarType', e.target.value)}
                        >
                            <option value="PERCENT">Percentage</option>
                            <option value="STEPS">Number of questions</option>
                        </select>
                    </PropsField>
                ) : null}

                <PropsToggle
                    checked={Boolean(survey.singleQuestionPerPage)}
                    onChange={(e) => setValue('singleQuestionPerPage', e.target.checked)}
                    label="Single question per page"
                />
            </PropsSection>

            <PropsSection title="Schedule & branding">
                <PropsField label="Start date">
                    <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white">
                            <i className="bi bi-calendar3 text-muted" />
                        </span>
                        <input
                            type="datetime-local"
                            className="form-control"
                            value={formatDateForInput(survey.startDate)}
                            onChange={handleDateChange('startDate')}
                        />
                    </div>
                </PropsField>

                <PropsField label="End date">
                    <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white">
                            <i className="bi bi-calendar3 text-muted" />
                        </span>
                        <input
                            type="datetime-local"
                            className="form-control"
                            value={formatDateForInput(survey.endDate)}
                            onChange={handleDateChange('endDate')}
                        />
                    </div>
                </PropsField>

                <PropsField label="Survey end message">
                    <textarea
                        className="form-control form-control-sm"
                        rows={3}
                        value={survey.surveyEndMessage || ''}
                        onChange={(e) => setValue('surveyEndMessage', e.target.value)}
                    />
                </PropsField>

                <PropsField label="Survey icon">
                    <IconPickerField
                        value={survey.icon || ''}
                        onChange={(icon) => setValue('icon', icon)}
                        label="Choose survey icon"
                    />
                </PropsField>

                <PropsField label="Icon color">
                    <select
                        className="form-select form-select-sm"
                        value={survey.iconColor || 'primary'}
                        onChange={(e) => setValue('iconColor', e.target.value)}
                    >
                        <option value="primary">Primary</option>
                        <option value="secondary">Secondary</option>
                        <option value="success">Success</option>
                        <option value="warning">Warning</option>
                        <option value="info">Info</option>
                        <option value="danger">Danger</option>
                    </select>
                </PropsField>
            </PropsSection>

            <PropsSection title="Completion">
                <PropsField label="Completion message" hint="Supports HTML">
                    <textarea
                        className="form-control form-control-sm"
                        rows={3}
                        value={survey.messageAfterComplete || ''}
                        onChange={(e) => setValue('messageAfterComplete', e.target.value)}
                    />
                </PropsField>

                <div className="ecx-survey-props__row">
                    <PropsField label="Redirect time (seconds)">
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            min={0}
                            value={survey.redirectTimeAfterMessage ?? 0}
                            onChange={(e) =>
                                setValue(
                                    'redirectTimeAfterMessage',
                                    e.target.value ? parseInt(e.target.value, 10) : 0
                                )
                            }
                        />
                    </PropsField>
                    <PropsField label="Redirect URL">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white">
                                <i className="bi bi-link-45deg text-muted" />
                            </span>
                            <input
                                type="url"
                                className="form-control"
                                placeholder="https://"
                                value={survey.redirectUrl || ''}
                                onChange={(e) => setValue('redirectUrl', e.target.value)}
                            />
                        </div>
                    </PropsField>
                </div>
            </PropsSection>

            <PropsSection title="Layout">
                <PropsField label="Page width" hint="e.g. 900px, 100%, 1200px">
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={survey.pageWidth || '900px'}
                        onChange={(e) => setValue('pageWidth', e.target.value)}
                    />
                </PropsField>

                <div className="ecx-survey-props__row">
                    <PropsField label="Header position">
                        <select
                            className="form-select form-select-sm"
                            value={survey.headerPosition || 'center'}
                            onChange={(e) => setValue('headerPosition', e.target.value)}
                        >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="end">Right</option>
                        </select>
                    </PropsField>
                    <PropsField label="Heading type">
                        <select
                            className="form-select form-select-sm"
                            value={survey.headingType || 'h1'}
                            onChange={(e) => setValue('headingType', e.target.value)}
                        >
                            <option value="h1">H1</option>
                            <option value="h2">H2</option>
                            <option value="h3">H3</option>
                            <option value="h4">H4</option>
                        </select>
                    </PropsField>
                </div>

                <div className="ecx-survey-props__row">
                    <PropsField label="Description position">
                        <select
                            className="form-select form-select-sm"
                            value={survey.descriptionPosition || 'center'}
                            onChange={(e) => setValue('descriptionPosition', e.target.value)}
                        >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="end">Right</option>
                        </select>
                    </PropsField>
                    <PropsField label="Form padding" hint="Desktop">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={survey.pagePaddingDesktop || '2rem'}
                            onChange={(e) => setValue('pagePaddingDesktop', e.target.value)}
                        />
                    </PropsField>
                </div>
            </PropsSection>

            <PropsSection title="Form card">
                <PropsToggle
                    checked={survey.formContainerShowCard}
                    onChange={(e) => setValue('formContainerShowCard', e.target.checked)}
                    label="Show form as card"
                />
                <PropsToggle
                    checked={survey.formContainerCardShadow}
                    onChange={(e) => setValue('formContainerCardShadow', e.target.checked)}
                    label="Card shadow"
                />

                <div className="ecx-survey-props__row">
                    <PropsField label="Container padding">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={survey.formContainerPadding || '2rem'}
                            onChange={(e) => setValue('formContainerPadding', e.target.value)}
                        />
                    </PropsField>
                    <PropsField label="Border radius" hint="e.g. 8px, 12px, auto">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={survey.formContainerBorderRadius || 'auto'}
                            onChange={(e) => setValue('formContainerBorderRadius', e.target.value)}
                        />
                    </PropsField>
                </div>

                <div className="ecx-survey-props__row">
                    <PropsField label="Background color" hint="CSS color or auto">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={survey.formContainerBackgroundColor || 'auto'}
                            onChange={(e) =>
                                setValue('formContainerBackgroundColor', e.target.value)
                            }
                        />
                    </PropsField>
                    <PropsField label="Description color">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={survey.descriptionColor || ''}
                            onChange={(e) => setValue('descriptionColor', e.target.value)}
                        />
                    </PropsField>
                </div>
            </PropsSection>

            <PropsSection title="Advanced">
                <PropsField label="Custom CSS">
                    <textarea
                        className="form-control form-control-sm ecx-survey-settings__code"
                        rows={4}
                        value={survey.customCss || ''}
                        onChange={(e) => setValue('customCss', e.target.value)}
                        spellCheck={false}
                    />
                </PropsField>

                <PropsField label="Additional settings (JSON)">
                    <textarea
                        className="form-control form-control-sm ecx-survey-settings__code"
                        rows={4}
                        value={survey.additionalSettings || ''}
                        onChange={(e) => setValue('additionalSettings', e.target.value)}
                        spellCheck={false}
                    />
                </PropsField>
            </PropsSection>
        </PropsShell>
    );
}

export default SurveySettings;
